import { Grid, Stack } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type {
  Bedrijf,
  Contactpersoon,
  Dienstverband,
  Functietitel,
  Locatie,
  Talentpool,
  Vacature,
  VacatureDetails,
  Vacaturestatus,
} from '../../api/typen.ts';
import { useVacatureMutaties } from '../../api/vacatures.ts';
import { FormulierDialoog } from '../../componenten/FormulierDialoog.tsx';
import {
  BedrijfVeld,
  ContactpersoonVeld,
  FunctietitelsVeld,
  LocatieVeld,
  TalentpoolsVeld,
} from '../../componenten/keuzevelden.tsx';
import { Keuzeveld, Tekstveld } from '../../componenten/velden.tsx';
import { leegNaarNull, naarGetal, naarTekst } from '../../helpers/formulier.ts';
import { dienstverbandLabels, opties, vacaturestatusLabels } from '../../helpers/labels.ts';
import { useVerzenden } from '../../hooks/useVerzenden.ts';

interface Waarden {
  titel: string;
  omschrijving: string;
  dienstverband: Dienstverband | '';
  urenMin: string;
  urenMax: string;
  salarisMin: string;
  salarisMax: string;
  startdatum: string;
  locatie: Locatie | null;
  bedrijf: Bedrijf | null;
  contactpersoon: Contactpersoon | null;
  status: Vacaturestatus;
  bron: string;
  bronUrl: string;
  functietitels: Functietitel[];
  talentpools: Talentpool[];
}

interface Props {
  open: boolean;
  onSluit: () => void;
  bestaand?: VacatureDetails | undefined;
  standaardBedrijf?: Bedrijf | undefined;
  onOpgeslagen?: ((vacature: Vacature) => void) | undefined;
}

const dienstverbandOpties = opties(dienstverbandLabels);
const statusOpties = opties(vacaturestatusLabels);

function beginwaarden(bestaand: VacatureDetails | undefined, standaardBedrijf: Bedrijf | undefined): Waarden {
  return {
    titel: bestaand?.titel ?? '',
    omschrijving: bestaand?.omschrijving ?? '',
    dienstverband: bestaand?.dienstverband ?? '',
    urenMin: naarTekst(bestaand?.urenMin),
    urenMax: naarTekst(bestaand?.urenMax),
    salarisMin: naarTekst(bestaand?.salarisMin),
    salarisMax: naarTekst(bestaand?.salarisMax),
    startdatum: bestaand?.startdatum ?? '',
    locatie: bestaand?.locatie ?? null,
    bedrijf: bestaand?.bedrijf ?? standaardBedrijf ?? null,
    contactpersoon: bestaand?.contactpersoon ?? null,
    status: bestaand?.status ?? 'concept',
    bron: bestaand?.bron ?? '',
    bronUrl: bestaand?.bronUrl ?? '',
    functietitels: bestaand?.functietitels.map((k) => k.functietitel) ?? [],
    talentpools: bestaand?.talentpools.map((k) => k.talentpool) ?? [],
  };
}

/**
 * Legt een vacature vast of wijzigt haar. Bij een nieuwe vacature kan meteen de indeling mee; de status wijzig
 * je daarna op de vacaturepagina.
 * @param props De bestaande vacature om te wijzigen, of een bedrijf dat alvast ingevuld is.
 * @returns De dialoog.
 */
export function VacatureFormulier({ open, onSluit, bestaand, standaardBedrijf, onOpgeslagen }: Props) {
  const mutaties = useVacatureMutaties();
  const formulier = useForm<Waarden>({ defaultValues: beginwaarden(bestaand, standaardBedrijf) });
  useEffect(() => {
    if (open) {
      formulier.reset(beginwaarden(bestaand, standaardBedrijf));
    }
  }, [open, bestaand, standaardBedrijf, formulier]);

  const bedrijf = formulier.watch('bedrijf');
  const contactpersoon = formulier.watch('contactpersoon');
  useEffect(() => {
    if (contactpersoon && contactpersoon.bedrijfId !== bedrijf?.id) {
      formulier.setValue('contactpersoon', null);
    }
  }, [bedrijf, contactpersoon, formulier]);

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    async (waarden) => {
      const gegevens = {
        titel: waarden.titel,
        omschrijving: leegNaarNull(waarden.omschrijving),
        dienstverband: waarden.dienstverband || null,
        urenMin: naarGetal(waarden.urenMin),
        urenMax: naarGetal(waarden.urenMax),
        salarisMin: naarGetal(waarden.salarisMin),
        salarisMax: naarGetal(waarden.salarisMax),
        startdatum: leegNaarNull(waarden.startdatum),
        locatieId: waarden.locatie?.id ?? null,
        bedrijfId: waarden.bedrijf?.id ?? null,
        contactpersoonId: waarden.contactpersoon?.id ?? null,
        bron: leegNaarNull(waarden.bron),
        bronUrl: leegNaarNull(waarden.bronUrl),
      };
      const vacature = bestaand
        ? await mutaties.wijzig.mutateAsync({ id: bestaand.id, invoer: gegevens })
        : await mutaties.maak.mutateAsync({
            ...gegevens,
            status: waarden.status,
            functietitelIds: waarden.functietitels.map((item) => item.id),
            talentpoolIds: waarden.talentpools.map((item) => item.id),
          });
      onOpgeslagen?.(vacature);
    },
    { melding: bestaand ? 'Vacature gewijzigd' : 'Vacature vastgelegd', naSucces: onSluit },
  );

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Vacature wijzigen' : 'Nieuwe vacature'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        maxWidth="md"
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <Tekstveld naam="titel" label="Titel" verplicht autoFocus />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            {bestaand ? (
              <Keuzeveld naam="dienstverband" label="Dienstverband" opties={dienstverbandOpties} />
            ) : (
              <Keuzeveld naam="status" label="Status" opties={statusOpties} verplicht />
            )}
          </Grid>
          {!bestaand && (
            <Grid size={{ xs: 12, sm: 4 }}>
              <Keuzeveld naam="dienstverband" label="Dienstverband" opties={dienstverbandOpties} />
            </Grid>
          )}
          <Grid size={{ xs: 6, sm: 2 }}>
            <Tekstveld naam="urenMin" label="Uren min." type="number" min={1} max={168} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Tekstveld naam="urenMax" label="Uren max." type="number" min={1} max={168} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Tekstveld naam="salarisMin" label="Salaris min." type="number" stap="0.01" min={0} />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Tekstveld naam="salarisMax" label="Salaris max." type="number" stap="0.01" min={0} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="startdatum" label="Startdatum" type="date" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <LocatieVeld naam="locatie" label="Werklocatie" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <BedrijfVeld naam="bedrijf" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <ContactpersoonVeld
              naam="contactpersoon"
              bedrijfId={bedrijf?.id}
              uitgeschakeld={!bedrijf}
              hulptekst={bedrijf ? undefined : 'Kies eerst een bedrijf'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="bron" label="Bron" placeholder="Bijv. eigen werving, vacaturesite" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Tekstveld naam="bronUrl" label="Link naar de bron" type="url" />
          </Grid>
          <Grid size={12}>
            <Tekstveld naam="omschrijving" label="Omschrijving" meerregelig rijen={5} />
          </Grid>
          {!bestaand && (
            <Grid size={12}>
              <Stack spacing={2}>
                <FunctietitelsVeld naam="functietitels" label="Gezochte functietitels" />
                <TalentpoolsVeld naam="talentpools" />
              </Stack>
            </Grid>
          )}
        </Grid>
      </FormulierDialoog>
    </FormProvider>
  );
}
